import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Modal, Skeleton, Spin, message } from 'antd';
import {
    CaretDown,
    House,
    PencilSimple,
    SunHorizon,
    BatteryCharging,
    ListBullets,
    Eye,
    CurrencyDollar,
    ArrowClockwiseIcon,
    Hand,
    WarningCircle,
} from '@phosphor-icons/react';
import { useOneButtonStore, COMMON_CONSTANTS, type ChangeOrderLoanStatusRow } from '../../../../store/oneButtonStore';
import { useViewerStore } from '../../../../store/store';
import { roofTextureList } from '../../../../Constants';
import designServices, { getDesignImageUrl, getRoofSquaresFromSystemDesign } from '../../../../services/oneButtonServices/designServices';
import type { RetrieveSystemDesignResponse } from '../../../../services/oneButtonServices/designServices';
import { effectiveAnnualUsageKwhForPricing } from '../../../../helpers/utilityMonthlyDailyToAnnual';
import {
    annualProductionKwhFromSnapshot,
    coercePositiveKw,
    parseDesignDataRecord,
    resolveSolarAnnualProductionKwh,
    solarProductionRawFromSnapshot,
    systemSizeKwFromSnapshot,
} from '../../../../helpers/systemDesignSolarMetrics';
import type { SelectedDesignForPricing } from '../../../../store/oneButtonStore';
import DesignLossesModal from './DesignLossesModal';
import { ProductDesignsModal, type ProductDesignsTab } from './ProductDesignsModal';
import DesignHistoryModal from './DesignHistoryModal';
import DesignHistoryModalV2 from './DesignHistoryModalV2';
import './oneButton-css/ProductionsCard.css';
import './oneButton-css/DashboardModal.css';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import { SystemMetricsCard, type SystemMetricsCardProps } from './SystemMetricsCard';
import { ProductionsCardMergedSystemSection } from './ProductionsCardMergedSystemSection';
import { UtilityDetailsPopupModal } from './UtilityDetailsPopupModal';
import { getOneButtonCapabilityValue } from '../../../../services/oneButtonServices/capabilityService';
import { loanRowHasChangeOrderFlag } from './commerceGuideLogic';

/** Trinity / Angular `CheckNewDesignExist` localStorage gate (system-design refresh + force sync). */
export const CHECK_NEW_DESIGN_EXIST_STORAGE_KEY = 'CheckNewDesignExist';

const SIGNIFICANT_CHANGE_ORDER_STATUSES = new Set(['Signed', 'Sent', 'Generated']);

/** Trinity `UpdateLoanStatus` field for clearing `Is_System_Design_Updated` on change-order rows (parity with Angular system-design refresh). */
const UPDATE_LOAN_STATUS_SYSTEM_DESIGN_FIELD = 'SystemDesign';
const { SolarProductName, RoofProductName, BatteryProductName, RRRProductName } = COMMON_CONSTANTS;

function normPartnerName(s: string | undefined | null): string {
    return String(s ?? '')
        .replace(/\s+/g, '')
        .toLowerCase();
}

function normalizeLoanStatusRows(raw: unknown): ChangeOrderLoanStatusRow[] {
    if (Array.isArray(raw)) return raw as ChangeOrderLoanStatusRow[];
    if (raw != null && typeof raw === 'object') return [raw as ChangeOrderLoanStatusRow];
    return [];
}

function readCheckNewDesignExistFromStorage(numericOpportunityId: string | null | undefined): boolean {
    if (typeof window === 'undefined') return false;
    const readKey = (key: string): boolean => {
        try {
            const v = localStorage.getItem(key);
            if (v == null) return false;
            const t = v.trim();
            if (!t) return false;
            const low = t.toLowerCase();
            if (low === 'false' || low === '0' || low === 'no') return false;
            if (low === 'true' || low === '1' || low === 'yes') return true;
            try {
                const p = JSON.parse(t) as unknown;
                if (typeof p === 'boolean') return p;
                if (p && typeof p === 'object') {
                    const o = p as Record<string, unknown>;
                    if (typeof o.exists === 'boolean') return o.exists;
                    if (typeof o.CheckNewDesignExist === 'boolean') return o.CheckNewDesignExist;
                }
            } catch {
                /* not JSON */
            }
            return false;
        } catch {
            return false;
        }
    };
    if (readKey(CHECK_NEW_DESIGN_EXIST_STORAGE_KEY)) return true;
    if (numericOpportunityId) {
        return readKey(`${CHECK_NEW_DESIGN_EXIST_STORAGE_KEY}_${numericOpportunityId}`);
    }
    return false;
}

function clearCheckNewDesignExistFromStorage(numericOpportunityId: string | null | undefined): void {
    if (typeof window === 'undefined') return;
    try {
        localStorage.removeItem(CHECK_NEW_DESIGN_EXIST_STORAGE_KEY);
        if (numericOpportunityId) {
            localStorage.removeItem(`${CHECK_NEW_DESIGN_EXIST_STORAGE_KEY}_${numericOpportunityId}`);
        }
    } catch {
        /* ignore */
    }
}

export function opportunityHasSolarFinancePartner(
    opportunityDetailsArg: any,
    partnerDisplayName: string,
): boolean {
    const op = opportunityDetailsArg?.OpportunityProduct ?? opportunityDetailsArg?.opportunityProduct;
    if (!Array.isArray(op)) return false;
    const target = normPartnerName(partnerDisplayName);
    return op.some(
        (product: any) =>
            product.Product_Type_Name === COMMON_CONSTANTS.SolarProductName &&
            normPartnerName(product.Finance_Partner) === target,
    );
}

function findLoanRowForPartner(rows: ChangeOrderLoanStatusRow[], partnerDisplayName: string): ChangeOrderLoanStatusRow | undefined {
    const target = normPartnerName(partnerDisplayName);
    return rows.find((x) => normPartnerName(x.Finance_Partner_Name ?? x.finance_Partner_Name) === target);
}

function shouldPromptForceSyncBeforeSystemDesignRefresh(params: {
    opportunityDetailsArg: any;
    loanRows: ChangeOrderLoanStatusRow[];
}): boolean {
    const { opportunityDetailsArg, loanRows } = params;
    const oppId = opportunityDetailsArg?.Opportunity_Id ?? null;
    
    // if (!readCheckNewDesignExistFromStorage(oppId != null ? String(oppId) : null)) {
    //     return false;
    // }

    const partnersToCheck: string[] = [];
    if (opportunityHasSolarFinancePartner(opportunityDetailsArg, COMMON_CONSTANTS.LightreachPartnerName)) {
        partnersToCheck.push(COMMON_CONSTANTS.LightreachPartnerName);
    }
    if (opportunityHasSolarFinancePartner(opportunityDetailsArg, COMMON_CONSTANTS.GoodLeapPartnerName)) {
        partnersToCheck.push(COMMON_CONSTANTS.GoodLeapPartnerName);
    }

    for (const partnerName of partnersToCheck) {
        const row = findLoanRowForPartner(loanRows, partnerName);

        if (!row || !loanRowHasChangeOrderFlag(row)) continue;

        const status = String(row.application_Status ?? '').trim();

        if (SIGNIFICANT_CHANGE_ORDER_STATUSES.has(status)) return true;
    }
    return false;
}

export interface ProductionsCardProps {
    loading?: boolean;
    /** Called when user clicks $ on Solar card with the design to use for pricing; parent should set selectedDesignForPricing then trigger fetch */
    onSolarPricingClick?: (design: SelectedDesignForPricing) => void;
    /** When embedded inside Products & finance hub, drop outer chrome */
    variant?: 'card' | 'embedded';
    /** Optional header label (e.g. "Design & Product Details"); defaults to "Products" */
    headerLabel?: string;
    /** Solar / roof / battery as a horizontal row (e.g. under System metrics) */
    sectionsLayout?: 'stack' | 'inline';
    /**
     * Single merged card: System metrics + Roof + Battery in one row.
     * Pass systemMetricsProps from the parent (same as standalone SystemMetricsCard).
     */
    mergeWithSystem?: boolean;
    systemMetricsProps?: Omit<SystemMetricsCardProps, 'embedded' | 'headerActions' | 'sectionFooterActions' | 'title' | 'extraClassName' | 'bentoLayout'>;
    /** Dedicated System rail panel — always show Solar / Roof / Battery cards. */
    alwaysExpanded?: boolean;
}

function getShingleImageUrl(selectedShingleId: string | null | undefined, selectedShingleAlt?: string | null): string | undefined {
    if (!selectedShingleId && !selectedShingleAlt) return undefined;
    const item = roofTextureList.find((t) => 
        (selectedShingleId && t.name === selectedShingleId) || 
        (selectedShingleAlt && t.alt === selectedShingleAlt)
    );
    if (!item?.img) return undefined;
    return (item.img as string) || (item.img as { default?: string })?.default;
}

function formatProductionValue(value: any): string {
    if (value == null || value === '') return '';
    const valStr = String(value);
    const numericValue = parseFloat(valStr.replace(/kWh/gi, '').trim());
    if (isNaN(numericValue)) return valStr;
    return (numericValue * 1000).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }) + ' kWh';
}

function mapRetrieveToSavePayload(response: RetrieveSystemDesignResponse): Record<string, unknown> {
    const activeSolar = (response?.OpportunityProductsFinanceInfo || []).find(
        (item) => item?.ActiveFlag && item?.Product_Type_Name === 'Solar'
    );

    // Keep save payload aligned with Angular's retrieve->save mapping path.
    return {
        Opportunity_Id: response?.Opportunity_Id,
        OpportunityInterest: response?.OpportunityInterest,
        SalesForce_Id: response?.SalesForce_Id,
        SolarProductId: response?.SolarProductId,
        SolarUtility: response?.SolarUtility,
        BatteryInformation: response?.BatteryInformation,
        BatteryProductId: response?.BatteryProductId,
        Finance_Partner_Sync_CO_LeadID: response?.Finance_Partner_Sync_CO_LeadID,
        BatteryOnly: response?.BatteryOnly,
        BatteryOnlyProductId: response?.BatteryOnlyProductId,
        RoofOnly: response?.RoofOnly,
        RoofProductId: response?.RoofProductId,
        Roof: response?.Roof,
        RoofOnlyProductId: response?.RoofOnlyProductId,
        Multiplier: response?.Multiplier,
        SystemSize: response?.SystemSize,
        SystemSizeDouble: response?.SystemSizeDouble,
        Production: response?.Production,
        ProductionDouble: response?.ProductionDouble,
        IsDomestic: response?.IsDomestic ?? false,
        FinanceMethod: activeSolar?.Finance_Method_Name || response?.FinanceMethod || '',
        designJson: JSON.stringify(response?.DesignArray || []),
        isManual: response?.isManual ?? false,
    };
}

export const ProductionsCard: React.FC<ProductionsCardProps> = ({
    loading = false,
    onSolarPricingClick,
    variant = 'card',
    headerLabel = 'Products',
    sectionsLayout = 'stack',
    mergeWithSystem = false,
    systemMetricsProps,
    alwaysExpanded = false,
}) => {
    const embedded = variant === 'embedded';
    const inlineSections = sectionsLayout === 'inline' || mergeWithSystem;
    const selectedFinanceCells = useOneButtonStore((s) => s.selectedFinanceCells);
    const priceQuoteOverrides = useOneButtonStore((s) => s.priceQuoteOverrides);
    const systemDesign = useOneButtonStore((s) => s.systemDesign);
    const designImage = useOneButtonStore((s) => s.designImage);
    const systemDesignLosses = useOneButtonStore((s) => s.systemDesignLosses);
    const opportunityID = useOneButtonStore((s) => s.opportunityID);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const batteryModelData = useOneButtonStore((s) => s.batteryModelData);
    const setBatteryModelData = useOneButtonStore((s) => s.setBatteryModelData);
    const setSelectedDesignForPricing = useOneButtonStore((s) => s.setSelectedDesignForPricing);
    const solarUtilityData = useOneButtonStore((s) => s.solarUtilityData);
    const SFannualUsage = useViewerStore((s) => s.SFannualUsage);
    const { setSystemDesign, setSystemDesignLosses, setDesignImage, setDesignHistory, setPriceQuoteDetails, setQuotesRefreshTrigger, theme } = 
        useOneButtonStore();

    const onebuttonCapabilities = useOneButtonStore((s) => s.onebuttonCapabilities);
    const designInfoAccess = getOneButtonCapabilityValue(onebuttonCapabilities, 'UI_Design_Info_Access');
    const isDesignInfoDisabled = designInfoAccess !== 'Edit' && designInfoAccess !== 'View';

    /** Products section is always open so Solar, Roof, Battery cards are always visible */
    const [productDesignsOpen, setProductDesignsOpen] = useState(false);
    const [productDesignsTab, setProductDesignsTab] = useState<ProductDesignsTab>('solar');
    const [lossesModalOpen, setLossesModalOpen] = useState(false);
    const [designHistoryOpen, setDesignHistoryOpen] = useState(false);
    const [designHistoryV2Open, setDesignHistoryV2Open] = useState(false);
    const [utilityPopupOpen, setUtilityPopupOpen] = useState(false);

    const [forceSyncConfirmOpen, setForceSyncConfirmOpen] = useState(false);
    /** True while `finishForceSyncConfirm` API sequence is in flight (modal OK/Cancel). */
    const [forceSyncConfirmLoading, setForceSyncConfirmLoading] = useState(false);

    const [isBatteryInfoUpdated, setIsBatteryInfoUpdated] = useState(false);

    const forceSyncConfirmResolveRef = useRef<((v: boolean) => void) | null>(null);
    const forceSyncNumericOppIdRef = useRef<string | null>(null);

    const openProductDesigns = (tab: ProductDesignsTab) => {
        setProductDesignsTab(tab);
        setProductDesignsOpen(true);
    };

    const handleEditBatteryClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        openProductDesigns('battery');
    };
    
    const opportunityInterest = opportunityDetails?.OpportunityInterest;
    const roofSquaredFromDesign = useMemo(() => getRoofSquaresFromSystemDesign(systemDesign), [systemDesign]);
    const sd = (systemDesign || {}) as any;
    const designData = useMemo(
        () => parseDesignDataRecord((systemDesign || {}) as Record<string, unknown>),
        [systemDesign],
    );
    /** Production raw from design API only (DesignArray / Design_Data); used in Design array modal. */
    const productionKwhRaw = useMemo(() => {
        const snap = (systemDesign || {}) as Record<string, unknown>;
        return solarProductionRawFromSnapshot(snap, designData);
    }, [systemDesign, designData]);

    const utilitySolarBase = useMemo(
        () => (solarUtilityData?.SolarUtility ?? {}) as Record<string, unknown>,
        [solarUtilityData],
    );
    const utilityAnnualKwhResolved = useMemo(
        () => effectiveAnnualUsageKwhForPricing(utilitySolarBase, SFannualUsage),
        [utilitySolarBase, SFannualUsage],
    );

    /** Solar card: RetrieveSystemDesign snapshot only — not live 3D preview. */
    const systemSizeKwFromDesignOnly = useMemo(() => {
        const snap = (systemDesign || {}) as Record<string, unknown>;
        return systemSizeKwFromSnapshot(snap, designData);
    }, [systemDesign, designData]);

    /** Design snapshot first (aligned with system size); then 3D; same resolver as merged System Design card. */
    const solarCardProductionDisplay = useMemo(() => {
        const snap = (systemDesign || {}) as Record<string, unknown>;
        const multRaw = snap.Multiplier ?? snap.multiplier;
        const multNum =
            multRaw != null && Number.isFinite(Number(multRaw)) ? Number(multRaw) : null;
        const kwh = resolveSolarAnnualProductionKwh({
            sd: snap,
            designData,
            systemKw: systemSizeKwFromDesignOnly,
            multiplier: multNum,
            snapshotOnly: true,
        });
        if (kwh != null && kwh > 0) {
            return `${kwh.toLocaleString()} kWh`;
        }
        const formatted = productionKwhRaw != null ? formatProductionValue(productionKwhRaw) : '';
        return formatted || null;
    }, [
        systemDesign,
        designData,
        systemSizeKwFromDesignOnly,
        productionKwhRaw,
    ]);

    const multiplier = sd.Multiplier ?? null;
    const designImageUrl = opportunityID && (designImage?.Id ?? designImage?.Name)
        ? getDesignImageUrl(opportunityID, designImage?.Id ?? undefined)
        : null;

    const batteryData = useMemo(() => {
        const batteryInfo = sd.BatteryInformation;
        let rawMfg = '—';
        let rawName = '—';
        let rawQty = '—';
        

        if (batteryInfo && (batteryInfo.Manufacturer || batteryInfo.Name || batteryInfo.Quantity)) {
            rawMfg = batteryInfo.Manufacturer || '—';
            rawName = batteryInfo.Name || '—';
            rawQty = batteryInfo.Quantity || '—';
        } else {
            const batteries = sd.DesignBattery || sd.Batteries;
            if (Array.isArray(batteries) && batteries.length > 0) {
                const b = batteries[0];
                rawMfg = b.Manufacturer || b.ManufacturerName || '—';
                rawName = b.Name || b.ModelName || '—';
                rawQty = b.Quantity || batteries.length || '—';
            }
        }

        let displayName = rawName;
        if (batteryModelData && rawName !== '—') {
            const found = batteryModelData.find((m: any) => 
                String(m.Value || m.value || m.id || m.ID || '') === String(rawName) || 
                String(m.Text || m.label || '') === String(rawName)
            );
            if (found) displayName = found.Text || found.label || rawName;
        }

        return {
            manufacturer: rawMfg,
            name: displayName,
            quantity: rawQty,
            rawName: rawName
        };
    }, [sd, batteryModelData]);

    const lastFetchedMfgRef = React.useRef<string>("");

    useEffect(() => {
        const mfg = batteryData.manufacturer;
        if (!mfg || mfg === '—' || mfg === '0') return;

        localStorage.setItem('CheckNewDesignExist',"true");
        // Skip if we already fetched for this manufacturer AND names are resolved
        const nameIsId = batteryData.name !== '—' && !isNaN(Number(batteryData.name));
        if (mfg === lastFetchedMfgRef.current && !nameIsId) return;

        const products = opportunityDetails?.OpportunityProduct || opportunityDetails?.opportunityProduct || [];
        const batteryItem = products.find((p: any) => p.Product_Type_Name === 'Battery');
        const partnerId = batteryItem?.Finance_Partner_Id || '0';
        
        lastFetchedMfgRef.current = mfg;
        designServices.getBatteryModel(mfg, partnerId).then((res: any) => {
            const data = res?.data || res || [];
            if (Array.isArray(data)) {
                setBatteryModelData(data);
            }
        }).catch(err => {
            console.error("ProductionsCard model fetch error", err);
            lastFetchedMfgRef.current = ""; // allow retry if failed
        });
    }, [batteryData.manufacturer, opportunityDetails, setBatteryModelData]);

    const hasRoof = Array.from(selectedFinanceCells).some((key) => {
        const product = key.split('::')[1] || '';
        return ['Roof', 'Solar + Roof'].includes(product);
    });
    const hasBattery = Array.from(selectedFinanceCells).some((key) => {
        const product = key.split('::')[1] || '';
        return ['Battery', 'Battery + Roof', 'Solar + Battery'].includes(product);
    });

    /** Warn to pick finance cells only when opportunity has no matching product interest (OpportunityDetail API). */
    const showRoofSelectProductMessage =
        !hasRoof &&
        !(
            opportunityInterest?.RR ||
            opportunityInterest?.RRR ||
            opportunityInterest?.Roof ||
            opportunityInterest?.RoofOnly
        );
    const showBatterySelectProductMessage =
        !hasBattery &&
        !(opportunityInterest?.Battery || opportunityInterest?.BatteryOnly);

    /** Match warning visibility: edit when not prompting to choose a product (finance cell OR opportunity interest). */
    const showRoofEditButton = !showRoofSelectProductMessage;
    const showBatteryEditButton = !showBatterySelectProductMessage;

    /** When battery finance warning shows, show “—” placeholders instead of design-derived stats. */
    const batterySectionStats = useMemo(() => {
        if (showBatterySelectProductMessage) {
            return { blankForWarning: true as const };
        }
        const mfg = String(batteryData.manufacturer || '').trim();
        const showManufacturerRow = !!(mfg && mfg !== '—');
        return {
            blankForWarning: false as const,
            manufacturer: batteryData.manufacturer,
            quantity: batteryData.quantity,
            name: batteryData.name,
            showManufacturerRow,
        };
    }, [showBatterySelectProductMessage, batteryData]);

    const roofHeaderShingleUrl = useMemo(() => {
        const id = priceQuoteOverrides?.selectedShingleId || (!Array.isArray(sd?.Roof) ? sd?.Roof?.ProductNumber : undefined);
        const alt = priceQuoteOverrides?.selectedShingleAlt || (!Array.isArray(sd?.Roof) ? sd?.Roof?.ShingleColor : undefined);
        return getShingleImageUrl(id, alt);
    }, [priceQuoteOverrides?.selectedShingleId, priceQuoteOverrides?.selectedShingleAlt, sd?.Roof?.ProductNumber, sd?.Roof?.ShingleColor]);

    const displayRoofPpsf = useMemo(() => {
        if (priceQuoteOverrides?.pricePerSqFt != null) return priceQuoteOverrides.pricePerSqFt;
        if (sd?.Roof && !Array.isArray(sd.Roof)) return (sd.Roof as any).PricePerSqft;
        return 0;
    }, [priceQuoteOverrides?.pricePerSqFt, sd]);

    const displayShingleName = useMemo(() => {
        if (priceQuoteOverrides?.selectedShingleAlt) return priceQuoteOverrides.selectedShingleAlt;
        if (sd?.Roof && !Array.isArray(sd.Roof)) return (sd.Roof as any).ShingleColor;
        return null;
    }, [priceQuoteOverrides?.selectedShingleAlt, sd?.Roof]);

    const mergedShingleSwatch = useMemo(() => {
        const alt = displayShingleName?.trim();
        const swatchByAlt: Record<string, string> = {
            'Dual Black': '#1a1a1a',
            'Charcoal Grey': '#4b5563',
            'Dual Grey': '#6b7280',
            'Dual Brown': '#6b4423',
            'Weatherwood': '#9a7b5c',
            'Sentinel Slate': '#5c6670',
            'Summit Grey': '#7b8494',
        };
        if (alt && swatchByAlt[alt]) return swatchByAlt[alt];
        const item = roofTextureList.find(
            (t) =>
                (priceQuoteOverrides?.selectedShingleId && t.name === priceQuoteOverrides.selectedShingleId) ||
                (alt && t.alt === alt),
        );
        return item ? swatchByAlt[item.alt] ?? '#3d3d3d' : '#3d3d3d';
    }, [priceQuoteOverrides?.selectedShingleId, displayShingleName]);

    /** Low / Standard use Roof.Low_Slope_Override & Normal_Slope_Override only (not LowSlope / NormalSlope). Null → — */
    const displayLowSlopeText = useMemo(() => {
        if (priceQuoteOverrides?.lowSlopeOverride != null && String(priceQuoteOverrides.lowSlopeOverride) !== '') {
            const n = Number(priceQuoteOverrides.lowSlopeOverride);
            if (!Number.isNaN(n)) return String(n);
        }
        if (sd?.Roof && !Array.isArray(sd.Roof)) {
            const v = (sd.Roof as Record<string, unknown>).Low_Slope_Override;
            if (v == null || v === '') return '—';
            const n = Number(v);
            return Number.isNaN(n) ? '—' : String(n);
        }
        return '—';
    }, [priceQuoteOverrides?.lowSlopeOverride, sd?.Roof]);

    const displayStandardSlopeText = useMemo(() => {
        if (priceQuoteOverrides?.normalSlopeOverride != null && String(priceQuoteOverrides.normalSlopeOverride) !== '') {
            const n = Number(priceQuoteOverrides.normalSlopeOverride);
            if (!Number.isNaN(n)) return String(n);
        }
        if (sd?.Roof && !Array.isArray(sd.Roof)) {
            const v = (sd.Roof as Record<string, unknown>).Normal_Slope_Override;
            if (v == null || v === '') return '—';
            const n = Number(v);
            return Number.isNaN(n) ? '—' : String(n);
        }
        return '—';
    }, [priceQuoteOverrides?.normalSlopeOverride, sd?.Roof]);

    /** When roof finance warning shows, show $0.00 PPSF and — for Low / Standard slopes. */
    const roofSectionDisplay = useMemo(() => {
        if (showRoofSelectProductMessage) {
            return {
                priceFormatted: '$0.00',
                lowSlopeDisplay: '—',
                standardSlopeDisplay: '—',
            };
        }
        return {
            priceFormatted: `$${Number(displayRoofPpsf || 0).toFixed(2)}`,
            lowSlopeDisplay: displayLowSlopeText,
            standardSlopeDisplay: displayStandardSlopeText,
        };
    }, [
        showRoofSelectProductMessage,
        displayRoofPpsf,
        displayLowSlopeText,
        displayStandardSlopeText,
    ]);

    const openPricingForDesign = () => {
        const snap = (systemDesign || {}) as Record<string, unknown>;
        const prodKwhAnnual =
            annualProductionKwhFromSnapshot(snap) ??
            (utilityAnnualKwhResolved != null && utilityAnnualKwhResolved > 0
                ? Math.round(utilityAnnualKwhResolved)
                : null) ??
            0;
        const sysKw =
            systemSizeKwFromDesignOnly != null && Number.isFinite(Number(systemSizeKwFromDesignOnly))
                ? Number(systemSizeKwFromDesignOnly)
                : coercePositiveKw(utilitySolarBase.SystemSize) ?? 0;
        const design: SelectedDesignForPricing = {
            designId: 'products-solar',
            categoryId: 'Products Solar',
            systemSizeKw: sysKw,
            estimatedAnnualProductionKwh: prodKwhAnnual,
            productionW: prodKwhAnnual,
            modules: undefined,
            roofCount: undefined,
            layoutKey: 'products-solar',
        };
        setSelectedDesignForPricing(design);
        onSolarPricingClick?.(design);
    };
    const [isRefreshing, setIsRefreshing] = useState(false);
    /** True for the whole refresh interaction (loan-status fetch, optional confirm modal, retrieve/save). */
    const [refreshInteractionLock, setRefreshInteractionLock] = useState(false);
    const [isEquipmentModified, setIsEquipmentModified] = useState(false);
    const refreshInFlightRef = React.useRef(false);

    /** Trinity `system-design` / Swal parity — confirm before retrieve+save when LR/GL change order + new design flag. */
    const showForceSyncConfirmationDialog = () =>
        new Promise<boolean>((resolve) => {
            forceSyncConfirmResolveRef.current = resolve;
            const isEquipmentModified :boolean = (systemDesign?.IsEquipmentModified && !systemDesign?.IsWithinTolerance && !systemDesign?.IsOutOfTolerance && !isBatteryInfoUpdated)?true:false;
            setIsEquipmentModified(isEquipmentModified);
            setForceSyncConfirmOpen(true);
        });

    const finishForceSyncConfirm = async (confirmed: boolean) => {
        setForceSyncConfirmLoading(true);
        setIsRefreshing(true);
        try {
            const r = forceSyncConfirmResolveRef.current;
            forceSyncConfirmResolveRef.current = null;
            const numericId = forceSyncNumericOppIdRef.current;

            const settle = (value: boolean) => {
                setForceSyncConfirmOpen(false);
                r?.(value);
            };

            if (!numericId) {
                settle(false);
                return;
            }

            /** Trinity `utility-info` / system-design `lightReachVoidQuote` — void LR quote before loan-status + refresh (GoodLeap has no equivalent here). */
            const opProducts = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
            const haveLightreachProduct = Array.isArray(opProducts)
                ? opProducts.some(
                      (product: any) => product.Finance_Partner === COMMON_CONSTANTS.LightreachPartnerName,
                  )
                : false;

            if (haveLightreachProduct && confirmed && !isEquipmentModified) {
                try {
                    await designServices.lightReachVoidQuote(numericId, opportunityDetails?.Finance_Partner_Sync_LeadID);
                } catch (e: unknown) {
                    const err = e as {
                        response?: { data?: { title?: string; Message?: string; errors?: Record<string, unknown> } };
                        message?: string;
                    };
                    const data = err?.response?.data;
                    let text =
                        String(data?.title ?? data?.Message ?? err?.message ?? '').trim() || 'Could not void Lightreach quote.';
                    if (data?.errors && typeof data.errors === 'object') {
                        const parts = Object.values(data.errors).flat();
                        if (parts.length > 0) {
                            text = Array.isArray(parts[0]) ? String((parts[0] as string[])[0]) : String(parts[0]);
                        }
                    }
                    message.error(text);
                    settle(false);
                    return;
                }
            }

            if (!isEquipmentModified) {
                try {
                    await quoteServices.contractUpdateLoanStatus(numericId, UPDATE_LOAN_STATUS_SYSTEM_DESIGN_FIELD, 0);
                } catch (e: unknown) {
                    const ax = e as { response?: { data?: { title?: string; Message?: string; errors?: unknown } }; message?: string };
                    let text =
                        String(ax?.response?.data?.title ?? ax?.response?.data?.Message ?? ax?.message ?? '').trim() ||
                        'Could not post system design override to Contract.';
                    const data = ax?.response?.data as { errors?: Record<string, unknown> } | undefined;
                    if (data?.errors && typeof data.errors === 'object') {
                        const parts = Object.values(data.errors).flat();
                        if (parts.length > 0) text = Array.isArray(parts[0]) ? String((parts[0] as string[])[0]) : String(parts[0]);
                    }
                    message.error(text);
                    settle(false);
                    return;
                }
            }

            try {
                await quoteServices.savePageChanges({
                    opportunityId: numericId,
                    pageName: 'Design',
                    changes:
                        'Force sync: user confirmed refreshing system design with an active Lightreach or Good Leap change order in a sent, generated, or signed contract state.',
                });
                await quoteServices.getPageChanges(numericId);
            } catch {
                /* Trinity checklist: best-effort */
            }

            try {
                await quoteServices.contractGetLoanStatusByOpportunity(numericId);
            } catch {
                /* Angular continues */
            }

            if (!confirmed || isEquipmentModified) {
                forceSyncNumericOppIdRef.current = null;
                await runRetrieveSaveAndQuoteAfterOverride(numericId, true, !isEquipmentModified);
                settle(false);
                return;
            }

            setQuotesRefreshTrigger(Date.now());
            settle(true);
        } finally {
            setForceSyncConfirmLoading(false);
            // Keep refresh spinner when handleRefresh will run retrieve/save after confirm.
            if (!confirmed || isEquipmentModified) {
                setIsRefreshing(false);
            }
        }
    };

    const runRetrieveSaveAndQuoteAfterOverride = async (
        id: string,
        consumeNewDesignFlag: boolean,
        disabledCreateLead?: boolean,
        /** Fresh RetrieveSystemDesign result when already fetched (e.g. handleRefresh); avoids stale closure on `systemDesign`. */
        retrievedDesign?: RetrieveSystemDesignResponse | null,
    ) => {
        const [refreshSystemDesignLosses, refreshDesignImage, refreshDesignHistory] = await Promise.all([
            designServices.SystemDesignLosses(id),
            designServices.GetDesignImage(id),
            designServices.GetDesignHistory(id)
        ]);

        setSystemDesignLosses(refreshSystemDesignLosses ?? null);
        setDesignImage(refreshDesignImage ?? null);
        setDesignHistory(refreshDesignHistory ?? null);

        const designForSave =
            retrievedDesign ?? (await designServices.RetrieveSystemDesign(id));
        if (designForSave) {
            setSystemDesign(designForSave);
            const payload = mapRetrieveToSavePayload(designForSave);
            try {
                const saveRes = await designServices.SaveSystemDesign(payload);
                if (saveRes) {
                    const getPriceQuote = await quoteServices.getRetrievePriceQuote(opportunityID || '');
                    if (getPriceQuote) setPriceQuoteDetails(getPriceQuote as any);
                    if (consumeNewDesignFlag) {
                        clearCheckNewDesignExistFromStorage(id);
                    }
                    //Implement Update Loan Status when opportunity product have Goodleap finance partner
                    if(opportunityID && opportunityHasSolarFinancePartner(opportunityDetails, COMMON_CONSTANTS.GoodLeapPartnerName)){
                        await quoteServices.contractUpdateLoanStatus(opportunityID, UPDATE_LOAN_STATUS_SYSTEM_DESIGN_FIELD, 0);
                        await quoteServices.contractGetLoanStatusByOpportunity(opportunityID);
                    }

                    if(opportunityHasSolarFinancePartner(opportunityDetails, COMMON_CONSTANTS.LightreachPartnerName) ||
                        opportunityHasSolarFinancePartner(opportunityDetails, COMMON_CONSTANTS.IGSPartnerName)){

                        const productCode = getNormalizationCode('Solar');
                        const opportunityProductType = useOneButtonStore.getState().productType;
                        const payload = {
                            productSelection: {
                                contractType: '',
                                Product_Category: opportunityProductType || productCode,
                                LeadForProduct: productCode
                            },
                            opportunityId: opportunityID
                        };
                        if(!disabledCreateLead) await quoteServices.createLeadSystem(payload)
                    }

                }
            } catch (saveError) {
                const getPriceQuote = await quoteServices.getRetrievePriceQuote(opportunityID || '');
                if (getPriceQuote) setPriceQuoteDetails(getPriceQuote as any);
                console.error("Save System Design after refresh failed:", saveError);
            }
        }
    };

    // Helper to map long product names/types to the short codes used by the finance partner APIs.
    const getNormalizationCode = (type: string) => {
        const t = (type || '').toLowerCase();
        if (t.includes(SolarProductName.toLowerCase())) return 'S';
        if (t.includes(RoofProductName.toLowerCase())) return 'R';
        if (t.includes(BatteryProductName.toLowerCase())) return 'B';
        if (t.includes(RRRProductName.toLowerCase())) return 'RRR';
        return 'S'; // Default to Solar if unknown
    };

    const handleRefresh = async () => {
        if (refreshInFlightRef.current) return;
        refreshInFlightRef.current = true;
        setRefreshInteractionLock(true);
        useOneButtonStore.getState().setSystemDesignRefreshing(true);
        const id = opportunityID || '';
        let consumeNewDesignFlag = false;
        let retrievedDesign: RetrieveSystemDesignResponse | null = null;
        try {
            const res = await designServices.checkNewDesignExist(id)
            if(res.data.Success && opportunityID){
                retrievedDesign = await designServices.RetrieveSystemDesign(id ?? '');
                if (retrievedDesign) setSystemDesign(retrievedDesign);
            } 
            if (id && mergeWithSystem) {
                let loanRows: ChangeOrderLoanStatusRow[] = [];
                try {
                    const rawLoan = await quoteServices.contractGetLoanStatusByOpportunity(id);
                    loanRows = normalizeLoanStatusRows(rawLoan);
                } catch {
                    loanRows = [];
                }

                if (
                    shouldPromptForceSyncBeforeSystemDesignRefresh({
                        opportunityDetailsArg: opportunityDetails,
                        loanRows,
                    })
                ) {
                    
                    forceSyncNumericOppIdRef.current = id;
                    const userConfirmed = await showForceSyncConfirmationDialog();
                    if (!userConfirmed) {
                        return;
                    }
                    consumeNewDesignFlag = true;
                } else {
                    if(opportunityID) await quoteServices.contractUpdateLoanStatus(opportunityID, UPDATE_LOAN_STATUS_SYSTEM_DESIGN_FIELD, 0);
                }

            }

            setIsRefreshing(true);
            await runRetrieveSaveAndQuoteAfterOverride(id, consumeNewDesignFlag, undefined, retrievedDesign);
            const now = Date.now();
            useOneButtonStore.setState({ pricingNetworkRefreshTrigger: now, systemDesignRefreshTrigger: now });
        } catch (error) {
            console.error("Refresh failed:", error);
        } finally {
            refreshInFlightRef.current = false;
            setIsRefreshing(false);
            setRefreshInteractionLock(false);
            useOneButtonStore.getState().setSystemDesignRefreshing(false);
        }
    };

    const productDesignsModalElement = (
        <ProductDesignsModal
            open={productDesignsOpen}
            onClose={() => setProductDesignsOpen(false)}
            initialTab={productDesignsTab}
            systemDesign={systemDesign}
            designImageUrl={designImageUrl}
            designImageName={designImage?.Name}
            productionKwh={productionKwhRaw ?? null}
            systemSizeKw={systemSizeKwFromDesignOnly ?? null}
            multiplier={multiplier}
            systemDesignLosses={systemDesignLosses}
            solarProductFinancePartner={opportunityDetails?.SolarProductFinancePartner}
            onOpenDesignHistoryV2={() => setDesignHistoryV2Open(true)}
            onOpenDesignHistory={() => setDesignHistoryOpen(true)}
            batteryData={batteryData}
        />
    );

    if (loading) {
        if (mergeWithSystem) {
            return (
                <>
                    <ProductionsCardMergedSystemSection
                        loading
                        alwaysExpanded={alwaysExpanded}
                        disabled={isDesignInfoDisabled}
                        sectionHeadActions={null}
                        showRoofSelectProductMessage={false}
                        showBatterySelectProductMessage={false}
                        displayShingleName={null}
                        onDesignSolar={() => openProductDesigns('solar')}
                        onDesignRoof={() => openProductDesigns('roof')}
                        onDesignBattery={() => openProductDesigns('battery')}
                        onUtility={() => {}}
                        onOpenProducts={() => {}}
                        onRoofDetails={() => openProductDesigns('roof')}
                    />
                    {productDesignsModalElement}
                </>
            );
        }
        return (
            <div
                className={`obd-card obd-card--products${inlineSections ? ' obd-card--products-inline' : ''} ${isDesignInfoDisabled ? 'obd-disabled-section' : ''}`}
            >
                <div className="obd-quotes__header" style={{ width: '100%', cursor: 'default' }}>
                    <span className="obd-card__label">{headerLabel}</span>
                    <CaretDown size={14} weight="bold" className="obd-quotes__chevron obd-quotes__chevron--open" />
                </div>
                <div className="obd-selected-layout">
                    <div
                        className={`obd-selected-products-sections${inlineSections ? ' obd-selected-products-sections--inline' : ''}`}
                    >
                        {/* Solar Skeleton */}
                        <div className="obd-product-section obd-product-section--solar">
                            <div className="obd-product-section__head" style={{ borderBottom: 'none' }}>
                                <Skeleton.Avatar active size="small" shape="square" style={{ marginRight: 8 }} />
                                <Skeleton.Input active size="small" style={{ width: 80 }} />
                            </div>
                            <div className="obd-solar-design" style={{ padding: '0 12px 12px' }}>
                                <Skeleton active paragraph={{ rows: 2, width: ['100%', '80%'] }} title={false} />
                                <div style={{ marginTop: 12, height: 100, background: 'rgba(255,255,255,0.05)', borderRadius: 8 }} className="obd-skeleton-pulse" />
                            </div>
                        </div>
                        {/* Roof Skeleton */}
                        <div className="obd-product-section obd-product-section--roof">
                            <div className="obd-product-section__head">
                                <Skeleton.Avatar active size="small" shape="square" style={{ marginRight: 8 }} />
                                <Skeleton.Input active size="small" style={{ width: 60 }} />
                            </div>
                            <div style={{ padding: '12px' }}>
                                <Skeleton active paragraph={{ rows: 3, width: ['90%', '70%', '85%'] }} title={false} />
                            </div>
                        </div>
                        {/* Battery Skeleton */}
                        <div className="obd-product-section obd-product-section--battery">
                            <div className="obd-product-section__head">
                                <Skeleton.Avatar active size="small" shape="square" style={{ marginRight: 8 }} />
                                <Skeleton.Input active size="small" style={{ width: 100 }} />
                            </div>
                            <div className="obd-solar-design" style={{ padding: '12px' }}>
                                <Skeleton active paragraph={{ rows: 2, width: ['100%', '90%'] }} title={false} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const mergedSectionHeadActions = (
        <div className="obd-metrics__header-actions">
            <div className="obd-metrics__action-group" role="group" aria-label="System design actions">
                <button
                    id="refresh-system-design-btn"
                    type="button"
                    className="obd-merged-system__chip-btn refresh-btn"
                    title="Refresh Design"
                    aria-label="Refresh Design"
                    disabled={refreshInteractionLock}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRefresh();
                    }}
                >
                    {isRefreshing ? (
                        <span className="refresh-loader" aria-hidden />
                    ) : (
                        <ArrowClockwiseIcon size={16} weight="bold" aria-hidden />
                    )}
                    <span>Refresh Design</span>
                </button>
            </div>
        </div>
    );

    const solarCardHeadActions = (
        <div
            className="obd-financing__toolbar obd-metrics__action-group obd-sd-card__head-actions"
            role="group"
            aria-label="Solar design shortcuts"
        >
            <button
                type="button"
                className="obd-merged-system__chip-btn"
                title="Load financing pricing for this design"
                aria-label="Load financing pricing"
                onClick={(e) => {
                    e.stopPropagation();
                    openPricingForDesign();
                }}
            >
                <CurrencyDollar size={16} weight="bold" aria-hidden />
                <span>Pricing</span>
            </button>
        </div>
    );

    const mergedRoofTitle = opportunityInterest?.RRR ? 'RRR' : 'Roof';

    if (mergeWithSystem) {
        return (
            <>
                <ProductionsCardMergedSystemSection
                    disabled={isDesignInfoDisabled}
                    alwaysExpanded={alwaysExpanded}
                    sectionHeadActions={mergedSectionHeadActions}
                    solarCardHeadActions={solarCardHeadActions}
                    showRoofSelectProductMessage={showRoofSelectProductMessage}
                    showBatterySelectProductMessage={showBatterySelectProductMessage}
                    roofTitle={mergedRoofTitle}
                    displayShingleName={displayShingleName}
                    shingleSwatch={mergedShingleSwatch}
                    onDesignSolar={() => openProductDesigns('solar')}
                    onDesignRoof={() => openProductDesigns('roof')}
                    onDesignBattery={() => openProductDesigns('battery')}
                    onUtility={() => setUtilityPopupOpen(true)}
                    onOpenProducts={() => {}}
                    onRoofDetails={() => openProductDesigns('roof')}
                />
                <UtilityDetailsPopupModal
                    open={utilityPopupOpen}
                    onClose={() => setUtilityPopupOpen(false)}
                />
                <DesignLossesModal
                    open={lossesModalOpen}
                    onClose={() => setLossesModalOpen(false)}
                    losses={systemDesignLosses}
                />
                {productDesignsModalElement}
                <DesignHistoryModalV2 open={designHistoryV2Open} onClose={() => setDesignHistoryV2Open(false)} opportunityId={opportunityDetails?.Opportunity_Id}/>
                <DesignHistoryModal open={designHistoryOpen} onClose={() => setDesignHistoryOpen(false)} />
                <Modal
                    title="Confirm system design refresh"
                    open={forceSyncConfirmOpen}
                    zIndex={13200}
                    centered
                    maskClosable={!forceSyncConfirmLoading}
                    closable={!forceSyncConfirmLoading}
                    okText="Continue"
                    cancelText="Cancel"
                    cancelButtonProps={{ style: { display: isEquipmentModified ? 'none' : 'block' } }}
                    rootClassName={`obd-theme-${theme} obd-productions-force-sync-confirm`}
                    modalRender={(modal) => (
                        <div className="obd-productions-force-sync-confirm__shell">
                            {modal}
                            {forceSyncConfirmLoading ? (
                                <div
                                    className="obd-dashboard-modal__loading-overlay"
                                    role="status"
                                    aria-live="polite"
                                    aria-busy="true"
                                >
                                    <div className="obd-dashboard-modal__loading-inner">
                                        <Spin size="default" />
                                        <span className="obd-dashboard-modal__loading-text">Processing…</span>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    )}
                    onOk={() => void finishForceSyncConfirm(true)}
                    onCancel={() => void finishForceSyncConfirm(false)}
                    afterClose={() => {
                        if (forceSyncConfirmResolveRef.current) {
                            forceSyncConfirmResolveRef.current(false);
                            forceSyncConfirmResolveRef.current = null;
                        }
                        forceSyncNumericOppIdRef.current = null;
                    }}
                >
                    <>
                        { systemDesign?.IsOutOfTolerance ? (
                               <p>Either the System Size or Annual Production Estimate have been updated and fall outside the Lightreach tolerance threshold. This change will require a customer re-sign, and the current active contract and quote will be voided. You will need to create a new quote for submission.</p>
                            ): systemDesign?.IsWithinTolerance ? (
                                <div>
                                    <b>Notice</b>: The System Size or Annual Production Estimate has been updated, but the change remains within Lightreach's tolerance threshold.No customer re-sign is required.
                                    <ul>
                                        <li> - To sync the design changes with Lightreach, click <b>OK</b>.</li>
                                        <li> - To save the design without syncing, click <b>Cancel</b>.</li>
                                    </ul>
                                    <b>Important</b>: If you click Cancel but plan to create a new quote, you must return to this design, save it, and sync it with the Lightreach portal by Clicking <b>“OK” before</b> generating the quote. This ensures the quote reflects the correct system size and accurate financial details.`
                                </div>
                            ): isBatteryInfoUpdated ? (
                                <div>
                                    
                                    <div><b>Notice</b>: Battery Qty has been changed. This will require a customer re-sign.</div>
                                    <ul>
                                        <li> - To sync the Design / Battery Qty changes with Lightreach, click <b>OK</b>.</li>
                                        <li> - To save the Design / Battery Qty changes without syncing, click <b>Cancel</b>.</li>
                                    </ul>
                                    <b>Important</b>: If you click Cancel but plan to create a new quote, you must return to this design, save it, and sync it with the Lightreach portal by Clicking <b>“OK” before</b> generating the quote. This ensures the quote reflects the correct system size and accurate financial details.`;
                                </div>
                            ) :
                            systemDesign?.IsEquipmentModified ? (
                                <div>
                                    You have updated the equipment for this design. A new change order will be created and can be submitted to Lightreach by clicking the ‘Submit Change Order’ button on the active quote in the Agreement page. This change does not require a customer re-sign.
                                </div>
                            ) :
                            <div>
                                <b>Notice</b>: System design has changed since the last update to Light reach, but these design changes have not yet been synced.
                                <ul>
                                    <li> To sync the design changes with Lightreach, click <b>OK</b>.</li>
                                    <li> To save the design without syncing, click <b>Cancel</b>.</li>
                                </ul>
                                <b>Important</b>: If you click Cancel but plan to create a new quote, you must return to this design, save it, and sync it with the Lightreach portal by Clicking <b>“OK” before</b> generating the quote. This ensures the quote reflects the correct system size and accurate financial details.
                            </div>
                        }
                    </>
                    
                </Modal>
            </>
        );
    }

    return (
        <div
            className={`obd-card obd-card--products${embedded ? ' obd-card--products-embedded' : ''}${inlineSections ? ' obd-card--products-inline' : ''} ${isDesignInfoDisabled ? 'obd-disabled-section' : ''}`}
        >
            {!embedded && (
                <div className="obd-quotes__header" style={{ width: '100%', cursor: 'default' }}>
                    <span className="obd-card__label">{headerLabel}</span>
                    <CaretDown size={14} weight="bold" className="obd-quotes__chevron obd-quotes__chevron--open" />
                </div>
            )}
            <div className="obd-selected-layout">
                    <div
                        className={`obd-selected-products-sections${inlineSections ? ' obd-selected-products-sections--inline' : ''}`}
                    >
                        {/* Solar, Roof, Battery always visible; show placeholders when data is undefined/null */}
                        <div className="obd-product-section obd-product-section--solar">
                                <div className="obd-product-section__head">
                                    <SunHorizon size={18} weight="regular" />
                                    <span>Solar</span>
                                    <div className="obd-product-section__head-actions">
                                        <button
                                            type="button"
                                            className="obd-utility__edit-btn obd-product-section__head-edit"
                                            title="View design array"
                                            aria-label="View design array"
                                            onClick={(e) => { e.stopPropagation(); openProductDesigns('solar'); }}
                                        >
                                            <Eye size={16} weight="regular" />
                                        </button>
                                        <button
                                            type="button"
                                            className="obd-utility__edit-btn obd-product-section__head-edit"
                                            title="Load financing pricing for this design"
                                            aria-label="Load financing pricing"
                                            onClick={(e) => { e.stopPropagation(); openPricingForDesign(); }}
                                        >
                                            <CurrencyDollar size={16} weight="regular" />
                                        </button>
                                    </div>
                                </div>
                                <div
                                    className="obd-solar-design obd-solar-design--clickable"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => openProductDesigns('solar')}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openProductDesigns('solar'); } }}
                                    aria-label="View design array"
                                >
                                    {(solarCardProductionDisplay != null ||
                                        systemSizeKwFromDesignOnly != null ||
                                        multiplier != null) && (
                                        <div className="obd-solar-design__stats">
                                            {solarCardProductionDisplay != null && (
                                                <div className="obd-solar-design__stat">
                                                    <span className="obd-solar-design__stat-label">Production</span>
                                                    <span className="obd-solar-design__stat-value">
                                                        {solarCardProductionDisplay}
                                                    </span>
                                                </div>
                                            )}
                                            {systemSizeKwFromDesignOnly != null && (
                                                <div className="obd-solar-design__stat">
                                                    <span className="obd-solar-design__stat-label">System size</span>
                                                    <span className="obd-solar-design__stat-value">
                                                        {`${Number(systemSizeKwFromDesignOnly).toFixed(2)} kW`}
                                                    </span>
                                                </div>
                                            )}
                                            {multiplier != null && Number.isFinite(multiplier) && (
                                                <div className="obd-solar-design__stat">
                                                    <span className="obd-solar-design__stat-label">Multiplier</span>
                                                    <span className="obd-solar-design__stat-value">{Number(multiplier).toFixed(2)}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {designImageUrl && (
                                        <div className="obd-solar-design__image-wrap">
                                            <img
                                                src={designImageUrl}
                                                alt={designImage?.Name ?? 'Design'}
                                                className="obd-solar-design__image"
                                            />
                                        </div>
                                    )}
                                    <div className="obd-solar-design__tags" onClick={(e) => e.stopPropagation()}>
                                        {systemDesignLosses != null && (
                                            <button
                                                type="button"
                                                className="obd-design-tag obd-design-tag--clickable"
                                                onClick={() => setLossesModalOpen(true)}
                                            >
                                                <ListBullets size={15} weight="bold" />
                                                <span>Losses</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <DesignLossesModal
                                    open={lossesModalOpen}
                                    onClose={() => setLossesModalOpen(false)}
                                    losses={systemDesignLosses}
                                />
                            </div>
                        <div className="obd-product-section obd-product-section--roof">
                                <div
                                    className={`obd-product-section__head obd-product-section__head--roof ${roofHeaderShingleUrl ? 'obd-product-section__head--shingle-bg' : ''}`}
                                    style={roofHeaderShingleUrl ? { backgroundImage: `url(${roofHeaderShingleUrl})` } : undefined}
                                >
                                    <House size={18} weight="regular" />
                                    <span>{opportunityInterest?.RRR ? 'RRR' : 'Roof'}</span>
                                    {showRoofEditButton && (
                                        <button
                                            type="button"
                                            className="obd-utility__edit-btn obd-product-section__head-edit"
                                            title="Edit roof details"
                                            aria-label="Edit roof details"
                                            onClick={() => openProductDesigns('roof')}
                                        >
                                            <PencilSimple size={16} weight="regular" />
                                        </button>
                                    )}
                                </div>
                                <div className="obd-roof-price">
                                    <span className="obd-roof-price__label">Price per sq ft</span>
                                    <span className="obd-roof-price__value">{roofSectionDisplay.priceFormatted}</span>
                                </div>
                                <div className="obd-roof-price">
                                    <span className="obd-roof-price__label">Low Roof Squares</span>
                                    <span className="obd-roof-price__value">{roofSectionDisplay.lowSlopeDisplay}</span>
                                </div>
                                <div className="obd-roof-price">
                                    <span className="obd-roof-price__label">Normal Roof Squares</span>
                                    <span className="obd-roof-price__value">{roofSectionDisplay.standardSlopeDisplay}</span>
                                </div>
                                {displayShingleName != null && displayShingleName !== '' && (
                                    <p className="obd-product-section__shingle-name" style={{ marginTop: 8 }}>
                                        <strong>{displayShingleName}</strong>
                                    </p>
                                )}
                                {showRoofSelectProductMessage && (
                                    <div className="obd-product-section__warning" role="status">
                                        <span className="obd-product-section__warning-icon" aria-hidden>
                                            <WarningCircle size={18} weight="duotone" />
                                        </span>
                                        <p className="obd-product-section__warning-text">
                                            Choose the Roof product from the Products section to modify the roofing details.
                                        </p>
                                    </div>
                                )}
                            </div>
                        <div className="obd-product-section obd-product-section--battery">
                                <div className="obd-product-section__head">
                                    <BatteryCharging size={18} weight="regular" />
                                    <span>Battery Design</span>
                                    {showBatteryEditButton && (
                                        <button
                                            type="button"
                                            className="obd-utility__edit-btn obd-product-section__head-edit"
                                            title="Edit battery details"
                                            aria-label="Edit battery details"
                                            onClick={handleEditBatteryClick}
                                        >
                                            <PencilSimple size={16} weight="regular" />
                                        </button>
                                    )}
                                </div>
                                <div className="obd-solar-design">
                                    <div className="obd-solar-design__stats">
                                        {batterySectionStats.blankForWarning ? (
                                            <>
                                                <div className="obd-solar-design__stat">
                                                    <span className="obd-solar-design__stat-label">Manufacturer</span>
                                                    <span className="obd-solar-design__stat-value">—</span>
                                                </div>
                                                <div className="obd-solar-design__stat">
                                                    <span className="obd-solar-design__stat-label">Quantity</span>
                                                    <span className="obd-solar-design__stat-value">—</span>
                                                </div>
                                                <div className="obd-solar-design__stat">
                                                    <span className="obd-solar-design__stat-label">Name</span>
                                                    <span
                                                        className="obd-solar-design__stat-value"
                                                        style={inlineSections ? undefined : { whiteSpace: 'nowrap' }}
                                                    >
                                                        —
                                                    </span>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                {batterySectionStats.showManufacturerRow && (
                                                    <div className="obd-solar-design__stat">
                                                        <span className="obd-solar-design__stat-label">Manufacturer</span>
                                                        <span className="obd-solar-design__stat-value">{batterySectionStats.manufacturer}</span>
                                                    </div>
                                                )}
                                                <div className="obd-solar-design__stat">
                                                    <span className="obd-solar-design__stat-label">Quantity</span>
                                                    <span className="obd-solar-design__stat-value">{batterySectionStats.quantity}</span>
                                                </div>
                                                <div className="obd-solar-design__stat">
                                                    <span className="obd-solar-design__stat-label">Name</span>
                                                    <span
                                                        className="obd-solar-design__stat-value"
                                                        style={inlineSections ? undefined : { whiteSpace: 'nowrap' }}
                                                    >
                                                        {batterySectionStats.name}
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                                {showBatterySelectProductMessage && (
                                    <div className="obd-product-section__warning" role="status">
                                        <span className="obd-product-section__warning-icon" aria-hidden>
                                            <WarningCircle size={18} weight="duotone" />
                                        </span>
                                        <p className="obd-product-section__warning-text">
                                            Choose the Battery product from the Products section to modify the battery details.
                                        </p>
                                    </div>
                                )}
                            </div>
                    </div>
                    {productDesignsModalElement}
                </div>
        </div>
    );
};

export default ProductionsCard;
